import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import gaHero from '../../assets/images/heroes/ga-hero-main.webp';
import ncHero from '../../assets/images/heroes/nc-hero-main.webp';
import './Resources.css';

const BRAND_HEROES = { gso: gaHero, 'apex-nc': ncHero, 'apex-tn': ncHero };

const VIEWMEDICA_URL = 'https://ondemand.viewmedica.com/8545/menu/';

export default function Resources() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const heroImg = BRAND_HEROES[brandKey] || gaHero;
  const baseDomain = brand?.domain || 'gaspineortho.com';

  const resourcesSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Resources | ${brand.name}`,
    url: `https://${baseDomain}/resources`,
    description: `Patient resources from ${brand.name} including blog, FAQs, core exercises, patient forms, and education videos.`
  };

  const cards = [
    { title: 'Blog', desc: 'Health articles and updates', icon: '📝', link: '/blog' },
    { title: t('section.faq', locale), desc: 'Common questions answered', icon: '❓', link: '/faqs' },
    { title: 'Core Exercises', desc: 'Recovery and strengthening exercises', icon: '🏋️', link: '/core-exercises' },
    { title: 'Media Center', desc: 'Videos and presentations', icon: '🎬', link: '/media-center' },
    { title: 'Patient Referral', desc: 'For law firms and referring providers', icon: '📋', link: '/resources/patient-referral' },
    { title: 'New Patient Forms', desc: 'Online intake and registration', icon: '📄', link: '/new-patient-forms' },
  ];

  return (
    <div className="resources-page">
      <Helmet>
        <title>Resources | {brand.name}</title>
        <meta name="description" content={`Patient resources from ${brand.name}: blog, FAQs, core exercises, patient forms, education videos, and more.`} />
        <meta property="og:title" content={`Resources | ${brand.name}`} />
        <meta property="og:description" content={`Helpful patient resources from ${brand.name}.`} />
        <meta property="og:url" content={`https://${baseDomain}/resources`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/resources`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/resources" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(resourcesSchema) }} />
      <section
        className="resources-hero glass-dark"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <h1>{t('section.resources', locale)}</h1>
          <p>{brand.name}</p>
        </div>
      </section>

      {/* Resource cards grid */}
      <section className="resources-grid container">
        {cards.map((card, i) => (
          <Link key={i} to={localizedPath(card.link, locale)} className="glass-card resource-card">
            <span className="resource-icon">{card.icon}</span>
            <h3>{card.title}</h3>
            <p>{card.desc}</p>
          </Link>
        ))}
      </section>

      {/* Patient Education Videos — ViewMedica */}
      <section className="container" style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px 60px' }}>
        <div
          className="glass-card"
          style={{ padding: 0, overflow: 'hidden', borderRadius: 16 }}
        >
          <div style={{ padding: '24px 28px 16px' }}>
            <h2 style={{ margin: 0, color: 'var(--color-primary)' }}>🎬 Patient Education Videos</h2>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
              Interactive video library covering procedures, conditions, and recovery topics.
              Powered by ViewMedica.
            </p>
          </div>
          {/* Responsive 16:9 iframe container */}
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              src={VIEWMEDICA_URL}
              title="Patient Education Videos — ViewMedica"
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
          <div style={{ padding: '12px 28px 20px', textAlign: 'right' }}>
            <a
              href={VIEWMEDICA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-btn glass-btn-outline"
              style={{ fontSize: '0.85rem' }}
            >
              Open in New Tab ↗
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container" style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 60px' }}>
        <div className="glass-panel" style={{ padding: 28, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 10 }}>❓ Have Questions?</h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: 20 }}>
            Browse our comprehensive FAQ covering sports injuries, car accidents, orthopedic conditions, and more.
          </p>
          <Link to={localizedPath('/faqs', locale)} className="glass-btn glass-btn-primary">
            View All FAQs
          </Link>
        </div>
      </section>
    </div>
  );
}
